'use client';

import { useEffect, useState } from 'react';
import { createClient } from '@/lib/local-api/client';
import { BadgePost, countUnseenPosts } from '@/lib/unseen-posts';

export function usePostBadges(userId: string | undefined, pathname: string) {
  const [counts, setCounts] = useState({ userId, community: 0, activity: 0 });
  useEffect(() => {
    if (!userId) return;
    const client = createClient();
    const key = `bizzbox:seen-posts:${userId}`;
    let stopped = false;
    let running = false;
    let dirty = false;
    let activity: BadgePost[] = [];
    let groups: BadgePost[] = [];
    let joined = new Set<string>();
    let friends = new Set<string>();
    let seen = { activity: new Set<string>(), community: new Set<string>() };
    function restore() {
      try {
        const stored = JSON.parse(localStorage.getItem(key) || '{}');
        for (const section of ['activity', 'community'] as const) {
          if (Array.isArray(stored[section])) stored[section].filter((id: unknown) => typeof id === 'string').forEach((id: string) => seen[section].add(id));
        }
      } catch { /* Keep in-memory read state if storage is unavailable. */ }
    }
    restore();
    function update() {
      if (stopped) return;
      setCounts({ userId, community: countUnseenPosts(groups, seen.community, userId!, (post) => joined.has(post.community_id || '')),
        activity: countUnseenPosts(activity, seen.activity, userId!, (post) => post.visibility == null || post.visibility === 'public' || friends.has(post.author_id)) });
    }
    async function refresh() {
      if (running) { dirty = true; return; }
      running = true;
      try {
        const results = await Promise.all([
          client.from('community_members').select('community_id').eq('user_id', userId),
          client.from('connections').select('user_id_1,user_id_2'),
          client.from('community_posts').select('id,author_id,visibility'),
        ]);
        if (stopped || results.some((result) => result.error)) return;
        joined = new Set(results[0].data.map((row: { community_id: string }) => row.community_id));
        friends = new Set<string>();
        results[1].data.forEach((row: { user_id_1: string; user_id_2: string }) => {
          if (row.user_id_1 === userId) friends.add(row.user_id_2);
          if (row.user_id_2 === userId) friends.add(row.user_id_1);
        });
        activity = results[2].data;
        if (joined.size) {
          const result = await client.from('group_posts').select('id,author_id,community_id').in('community_id', [...joined]);
          if (result.error) return;
          groups = result.data;
        } else groups = [];
        update();
      } finally {
        running = false;
        if (dirty && !stopped) { dirty = false; void refresh(); }
      }
    }
    void refresh();
    const channel = client.channel(`footer-posts-${userId}`);
    for (const table of ['community_posts', 'group_posts', 'community_members', 'connections']) {
      channel.on('postgres_changes', { event: '*', table }, () => void refresh());
    }
    channel.subscribe();

    const section = pathname === '/community-network' ? 'activity' : /^\/community-groups\/[^/]+$/.test(pathname) ? 'community' : null;
    const observer = section ? new IntersectionObserver((entries) => {
      if (document.hidden) return;
      let changed = false;
      for (const entry of entries) {
        if (entry.isIntersecting) {
          const id = entry.target.id.slice(5);
          if (id && !seen[section].has(id)) { seen[section].add(id); changed = true; }
        }
      }
      if (changed) {
        restore();
        try { localStorage.setItem(key, JSON.stringify({ activity: [...seen.activity], community: [...seen.community] })); } catch { /* In-memory fallback. */ }
        update();
      }
    }, { threshold: 0, rootMargin: '-65px 0px -80px 0px' }) : null;
    const observed = new WeakSet<Element>();
    function observePosts() {
      document.querySelectorAll('[id^="post-"]').forEach((element) => {
        if (!observed.has(element)) { observer?.observe(element); observed.add(element); }
      });
    }
    const mutations = new MutationObserver(observePosts);
    if (observer) { observePosts(); mutations.observe(document.body, { childList: true, subtree: true }); }
    function onStorage(event: StorageEvent) { if (event.key === key) { restore(); update(); } }
    function onVisible() {
      if (!document.hidden) {
        void refresh();
        document.querySelectorAll('[id^="post-"]').forEach((element) => { observer?.unobserve(element); observer?.observe(element); });
      }
    }
    window.addEventListener('storage', onStorage);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      client.removeChannel(channel);
      observer?.disconnect(); mutations.disconnect();
      window.removeEventListener('storage', onStorage);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [userId, pathname]);
  return counts.userId === userId ? counts : { community: 0, activity: 0 };
}
